/**
 * System › Message Library — the pure view logic.
 *
 * Everything here is a GROUPING over the already-loaded rows: which tabs to show,
 * what to flag in the Checks column, how a row reads. No business rules — the
 * severity suggestion and the customized flag are computed by the API (§1) and
 * arrive on the row.
 *
 * Kept separate from the page so each rule can be tested without rendering.
 */
import type {
  MessageCheck, MessageRow, MessageSourceKind, MessageStatusKind, MessageTranslation,
} from './types';

/** The OTHER live messages whose wording is identical to this one's.
 *
 *  Shared by the Same Wording panel and its tab count — two implementations of
 *  "identical" would eventually disagree, and a badge that contradicts the list
 *  under it is worse than no badge. */
export function sameWordingOf(
  detail: { oid: string; message: string | null } | null,
  rows: MessageRow[],
): MessageRow[] {
  const mine = detail ? normalizeText(detail.message) : '';
  if (!detail || !mine) return [];
  return rows.filter((r) => r.oid !== detail.oid && normalizeText(r.message) === mine);
}

/** A translation row still being composed — no server id until it is written. */
export type DraftRow = MessageTranslation & { draft?: true };

/** A staged translation worth writing: both required cells filled in. */
export const isCompleteDraft = (r: MessageTranslation) =>
  !!(r.language ?? '').trim() && !!(r.translation ?? '').trim();

/** An absent Source means the row came from ALPACA's own seeder. */
export function sourceOf(row: MessageRow): MessageSourceKind {
  return row.source ?? 'Legacy';
}

/** Retired wins; then the server's customized flag; else Active. "Customized" is
 *  a reading of two stored columns, not a stored status of its own. */
export function statusOf(row: MessageRow): MessageStatusKind {
  if (row.status === 'Retired') return 'Retired';
  return row.isCustomized ? 'Customized' : 'Active';
}

/** The severity to DISPLAY, and whether it is the admin's or merely suggested. */
export function severityOf(row: MessageRow): { value: string; suggested: boolean } {
  if (row.severity) return { value: row.severity, suggested: false };
  return { value: row.severitySuggestion ?? 'Info', suggested: true };
}

/** A message key, normalised for comparison.
 *
 *  Case-INSENSITIVE, because that is both the database collation and the only
 *  sane reading: a key is looked up by name, so two keys differing only in case
 *  are the same key. This is exactly how the three legacy collisions arose
 *  (`DeleteSuccess` / `Deletesuccess`), and it is what blocks the unique index. */
export function normalizeKey(key: string | null | undefined): string {
  return (key ?? '').trim().toLowerCase();
}

/** Wording, normalised for the "same wording" grouping. Case- and
 *  whitespace-insensitive, so `"Saved successfully."` and `" Saved
 *  Successfully. "` group together — which is the point, since the reason to
 *  surface them is to replace the set with one shared key. */
export function normalizeText(text: string | null | undefined): string {
  return (text ?? '').trim().replace(/\s+/g, ' ').toLowerCase();
}

/** How many live rows share each normalised key / wording.
 *
 *  Built ONCE per load and passed to the per-row helpers — computing it inside
 *  `checksOf` would make rendering the grid quadratic. */
export interface MessageIndex {
  keyCounts: Map<string, number>;
  textCounts: Map<string, number>;
}

export function buildIndex(rows: MessageRow[]): MessageIndex {
  const keyCounts = new Map<string, number>();
  const textCounts = new Map<string, number>();
  for (const r of rows) {
    const k = normalizeKey(r.messageKey);
    if (k) keyCounts.set(k, (keyCounts.get(k) ?? 0) + 1);
    const t = normalizeText(r.message);
    if (t) textCounts.set(t, (textCounts.get(t) ?? 0) + 1);
  }
  return { keyCounts, textCounts };
}

export function isDuplicateKey(row: MessageRow, index: MessageIndex): boolean {
  const k = normalizeKey(row.messageKey);
  return k.length > 0 && (index.keyCounts.get(k) ?? 0) > 1;
}

/** How many OTHER rows carry the same wording (0 when it is unique). */
export function sameWordingCount(row: MessageRow, index: MessageIndex): number {
  const t = normalizeText(row.message);
  if (!t) return 0;
  return Math.max(0, (index.textCounts.get(t) ?? 0) - 1);
}

/**
 * The quality observations flagged in the Checks column.
 *
 * Each one is something an admin can act on:
 *  - a duplicate key blocks the unique index and makes lookups ambiguous;
 *  - leading/trailing space shows up in the rendered message;
 *  - a positional `{0}` placeholder is the legacy style, replaced by named ones;
 *  - shared wording is a candidate for one `Common.*` key instead of many rows;
 *  - no Module means the message is not attributed to any screen.
 */
export function checksOf(row: MessageRow, index: MessageIndex): MessageCheck[] {
  const checks: MessageCheck[] = [];

  if (isDuplicateKey(row, index)) checks.push({ tone: 'alert', label: 'Duplicate key' });

  const msg = row.message ?? '';
  if (msg.length > 0 && msg.trim() !== msg) checks.push({ tone: 'warn', label: 'Extra space' });
  if (/\{\d+\}/.test(msg)) checks.push({ tone: 'info', label: 'Positional placeholder' });

  const same = sameWordingCount(row, index);
  if (same > 0) checks.push({ tone: 'info', label: `Same wording ×${same + 1}` });

  // Attribution, as ONE observation rather than two: a message nothing raises has
  // nowhere to belong, so saying "no module" as well would be noise. A shared
  // message is not missing a module either — no single module owns it.
  if (isUnused(row)) checks.push({ tone: 'mute', label: 'Not used in code' });
  else if (!moduleOf(row) && !isShared(row)) checks.push({ tone: 'mute', label: 'No module' });

  return checks;
}

/** The checks worth a chip in the grid.
 *
 *  The `mute` ones — "No module", "Not used in code" — are attribution facts, not
 *  problems with the message, and they are already their own tabs. Showing them as
 *  chips puts a grey pill on nearly every row and drowns the checks that DO need
 *  attention. They stay in {@link checksOf} so the column still filters, sorts and
 *  exports on them. */
export function visibleChecks(row: MessageRow, index: MessageIndex): MessageCheck[] {
  return checksOf(row, index).filter((c) => c.tone !== 'mute');
}

/** Is this message raised across more than one MODULE?
 *
 *  Module-level, not form-level: two screens inside Reporting are still Reporting's
 *  message, and its owner can reword it. Once QA/QC raises it too, nobody owns the
 *  wording — which is the case worth surfacing, and what the Module column shows as
 *  "Shared". The server makes the call (it has each form's module); this only reads
 *  the verdict. */
export function isShared(row: MessageRow): boolean {
  return row.derivedModule === 'Shared';
}

/** The module a row BELONGS to: the one an admin assigned, else the one the legacy
 *  code raises it in.
 *
 *  "Shared" is deliberately NOT a module — it means several modules raise the
 *  message, so none of them owns it. Those rows are counted by {@link isShared}
 *  instead, which is why this returns null for them. */
export function moduleOf(row: MessageRow): string | null {
  if (row.module) return row.module;
  if (row.derivedModule && row.derivedModule !== 'Shared') return row.derivedModule;
  return null;
}

/** Is this message never raised by legacy code?
 *
 *  True for 90 of the 390 live keys. They are not necessarily dead — the scan can
 *  only see call sites that pass a LITERAL key — but they are the rows to look at
 *  first when deciding what can retire with XAF. */
export function isUnused(row: MessageRow): boolean {
  return row.usedInScreens === 0;
}

/** Free-text search across the columns a user would search by. */
export function matchesSearch(row: MessageRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [row.msid, row.messageKey, row.message, row.module, row.bo, row.remark]
    .some((v) => (v ?? '').toLowerCase().includes(q));
}

/** MSID sorts NUMERICALLY. It is zero-padded text, so a plain string sort is
 *  only right while every id is the same width and breaks past 999. */
export function compareMsid(a: MessageRow, b: MessageRow): number {
  const na = Number.parseInt(a.msid ?? '', 10);
  const nb = Number.parseInt(b.msid ?? '', 10);
  if (Number.isNaN(na) && Number.isNaN(nb)) return (a.msid ?? '').localeCompare(b.msid ?? '');
  if (Number.isNaN(na)) return 1;
  if (Number.isNaN(nb)) return -1;
  return na - nb;
}

/** The rows the search shows, in MSID order.
 *
 *  Subset filtering (module, source, status, checks) is the shared grid's own
 *  per-column filter — there is no second filtering layer on this form. */
export function visibleRows(rows: MessageRow[], search: string): MessageRow[] {
  return rows.filter((r) => matchesSearch(r, search)).sort(compareMsid);
}

/**
 * Can this row's key be edited?
 *
 * Normally NO — every call site looks a message up by its key, so renaming one in
 * place would silently break them all. The one exception is a row already in a
 * duplicate pair: resolving those three legacy case-collisions requires editing
 * one of them, and until they are resolved the unique index cannot be installed.
 * The API enforces the same rule; this only decides whether the field is offered.
 */
export function canEditKey(row: MessageRow | null, index: MessageIndex): boolean {
  return row !== null && isDuplicateKey(row, index);
}
